// RosterPanel — the admin-only service-load roster surface (U9).
//
// A panel inside the Suggestions sub-view. Per service it shows the three
// trusted planning values the cloud_roster_evaluator worker materialized:
// true-max, show-condition max, and the coincidence-peak contribution. The
// event-level coincidence peak is the admin-only number — it is served by no
// other endpoint (R8).
//
// Four data_state renderings (R2/R3/R4/R9):
//   ok             — all three values.
//   no_data        — an explicit "no data" row, never omitted (R4).
//   pending        — distinct copy for "no show window declared" vs "declared
//                    but the show hasn't occurred yet" (R9): the operator's
//                    next action differs, so the two strings differ.
//   low_confidence — true-max shown, visibly marked, with the provenance
//                    reason (R3).
//
// The roster is the *planning* input; the existing audit table is the
// *current observed* view. They differ — that is expected (roster-vs-audit
// authority). A one-line note states this. No optimistic state — this is a
// read-only view (web/dyno-pwa/CLAUDE.md convention #7).
//
// See docs/plans/2026-05-21-001-feat-house-feed-load-baselines-plan.md (U9).

import { useEffect, useMemo, useState } from "react";
import { useRosterStore } from "@/stores/roster";
import type {
  RosterEntry, RosterProvenance, SafestLowerBound, Tier1Result,
} from "@/lib/types";
import { subsetCoincidence } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

function fmtA(v: number | null | undefined): string {
  if (v == null) return "—";
  return `${v.toFixed(0)}A`;
}

// "roster as of …" — a single honest freshness stamp (KD1).
function fmtStamp(iso: string | null): string {
  if (!iso) return "never";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  return new Date(t).toLocaleString();
}

// -----------------------------------------------------------------------------
// data_state — per-row presentation
// -----------------------------------------------------------------------------

const STATE_STYLE: Record<RosterEntry["data_state"], { label: string; cls: string }> = {
  ok: {
    label: "ok",
    cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-400",
  },
  pending: {
    label: "pending",
    cls: "border-sky-500/30 bg-sky-500/10 text-sky-400",
  },
  no_data: {
    label: "no data",
    cls: "border-border/60 bg-muted/40 text-muted-foreground",
  },
  low_confidence: {
    label: "low confidence",
    cls: "border-amber-500/30 bg-amber-500/10 text-amber-500",
  },
  moderate_confidence: {
    label: "moderate confidence",
    cls: "border-yellow-500/30 bg-yellow-500/10 text-yellow-400",
  },
};

function StateBadge({ state }: { state: RosterEntry["data_state"] }) {
  const s = STATE_STYLE[state];
  return (
    <span
      className={`inline-block rounded-md border text-[10px] uppercase tracking-wide px-1.5 py-0.5 ${s.cls}`}
    >
      {s.label}
    </span>
  );
}

// R9 — the two pending copy strings. The operator's next action differs:
//   - no window declared  → go declare a show window on the Load-in tab.
//   - declared, not yet occurred → wait for the show (or check the meter).
// The roster cannot itself tell the two apart, but the presence of a
// show-condition value distinguishes them: a declared-and-occurred show would
// have populated show_condition_max_a. With no occurred show,
// show_condition_max_a is null. We split on whether ANY declared show window
// exists for the event (passed in from the Suggestions view, which already
// knows).
const PENDING_COPY = {
  no_window:
    "No show window declared for this event. Declare doors/curfew on the " +
    "Load-in tab so the roster can compute a show-condition baseline.",
  not_occurred:
    "A show window is declared, but the show has not occurred yet (no " +
    "sustained post-doors load observed). Show-condition and coincidence " +
    "values appear once the show runs.",
} as const;

// R3 — a human-readable reason for a low-confidence true-max, derived from
// provenance. A too-short data span and a calibration-only blip are the two
// ways `tested` ends up false (roster_compute.compute_true_max).
function lowConfidenceReason(p: RosterProvenance | null): string {
  if (!p) return "true-max provenance unavailable";
  if (p.data_span_days < p.sampled_window_days - 1) {
    return (
      `telemetry only spans ${p.data_span_days.toFixed(1)} of the ` +
      `${p.sampled_window_days}-day lookback — too short to trust`
    );
  }
  return (
    "peak load did not sustain long enough to count as a real show " +
    `(${fmtA(p.peak_magnitude_a)} for ` +
    `${Math.round(p.peak_run_duration_s / 60)} min)`
  );
}

// -----------------------------------------------------------------------------
// Roster row
// -----------------------------------------------------------------------------

function RosterRow({
  entry, hasDeclaredShow, selected, onToggle, selectable,
}: {
  entry: RosterEntry;
  hasDeclaredShow: boolean;
  selected: boolean;
  onToggle: (membershipId: string) => void;
  // no_data + pending rows have no curves to composite, so selection is
  // disabled on them — the subset-coincidence endpoint would just list them
  // under missing_curves.
  selectable: boolean;
}) {
  const name = entry.service_name ?? entry.membership_id;

  if (entry.data_state === "no_data") {
    // R4 — an explicit "no data" row, never omitted.
    return (
      <TableRow>
        <TableCell />
        <TableCell className="font-medium">{name}</TableCell>
        <TableCell colSpan={2} className="text-xs text-muted-foreground">
          No attributable per-leg current series — this service is not
          ACUVIM-metered, so it cannot be summed into a planning baseline.
        </TableCell>
        <TableCell className="text-right">
          <StateBadge state="no_data" />
        </TableCell>
      </TableRow>
    );
  }

  if (entry.data_state === "pending") {
    // R9 — distinct copy depending on whether a window has been declared.
    return (
      <TableRow>
        <TableCell />
        <TableCell className="font-medium">{name}</TableCell>
        <TableCell colSpan={2} className="text-xs text-muted-foreground">
          {hasDeclaredShow ? PENDING_COPY.not_occurred : PENDING_COPY.no_window}
        </TableCell>
        <TableCell className="text-right">
          <StateBadge state="pending" />
        </TableCell>
      </TableRow>
    );
  }

  // ok or low_confidence — two numbers (true-max + show-condition), true-max
  // marked when low.
  const lowConf = entry.data_state === "low_confidence";
  return (
    <TableRow>
      <TableCell className="w-8">
        <input
          type="checkbox"
          aria-label={`Include ${name} in coincidence preview`}
          checked={selected}
          disabled={!selectable}
          onChange={() => onToggle(entry.membership_id)}
          className="cursor-pointer accent-emerald-500"
        />
      </TableCell>
      <TableCell className="font-medium">{name}</TableCell>
      <TableCell className="text-right">
        <span className={lowConf ? "text-amber-500" : ""}>
          {fmtA(entry.true_max_a)}
        </span>
        {lowConf && (
          <div className="text-[10px] text-amber-500/80 mt-0.5 text-right max-w-[16rem] ml-auto">
            {lowConfidenceReason(entry.true_max_provenance)}
          </div>
        )}
      </TableCell>
      <TableCell className="text-right text-sm">
        {fmtA(entry.show_condition_max_a)}
      </TableCell>
      <TableCell className="text-right">
        <StateBadge state={entry.data_state} />
      </TableCell>
    </TableRow>
  );
}

// -----------------------------------------------------------------------------
// Subset-coincidence preview — pick services, get a coincidence peak
// -----------------------------------------------------------------------------

type SubsetState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; data: Tier1Result }
  | { kind: "error"; message: string };

function SubsetCoincidencePreview({
  windowId, selectedIds, entryName,
}: {
  windowId: string;
  selectedIds: string[];
  entryName: (membershipId: string) => string;
}) {
  const [state, setState] = useState<SubsetState>({ kind: "idle" });

  async function fetchPeak() {
    if (selectedIds.length === 0) return;
    setState({ kind: "loading" });
    try {
      const data = await subsetCoincidence(windowId, {
        membership_ids: selectedIds,
      });
      setState({ kind: "ready", data });
    } catch (e) {
      const message = e instanceof Error ? e.message : "unknown error";
      setState({ kind: "error", message });
    }
  }

  // Reset preview when the selection changes — the displayed peak should
  // never describe a different selection than what's checked right now.
  useEffect(() => {
    setState({ kind: "idle" });
  }, [selectedIds.join(",")]);

  if (selectedIds.length === 0) {
    return (
      <div className="text-[11px] text-muted-foreground">
        Select two or more services above to preview their coincidence peak
        (the sum of their composite show-load curves at the moment the sum is
        highest). Single-service selections compute too, but the result is
        just that service's own show peak.
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-3 text-xs">
      <span className="text-muted-foreground">
        {selectedIds.length} selected
      </span>
      {state.kind === "idle" && (
        <Button size="sm" variant="outline" onClick={fetchPeak}>
          Compute coincidence peak
        </Button>
      )}
      {state.kind === "loading" && (
        <span className="text-muted-foreground">computing…</span>
      )}
      {state.kind === "error" && (
        <>
          <span className="text-red-400">Error: {state.message}</span>
          <Button size="sm" variant="outline" onClick={fetchPeak}>Retry</Button>
        </>
      )}
      {state.kind === "ready" && (
        <>
          <span>
            coincidence peak{" "}
            <span className="font-semibold text-emerald-400">
              {fmtA(state.data.coincidence_peak_a)}
            </span>
          </span>
          {state.data.missing_curves.length > 0 && (
            <span className="text-amber-500/80">
              ({state.data.missing_curves.length} of {selectedIds.length} have
              no show curves yet: {state.data.missing_curves.map(entryName).join(", ")})
            </span>
          )}
          <Button size="sm" variant="outline" onClick={fetchPeak}>
            Recompute
          </Button>
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Safest lower-bound surface (KD6) — names the un-summed no_data services.
// -----------------------------------------------------------------------------

function SafestLowerBoundNote({ lb }: { lb: SafestLowerBound }) {
  if (!lb.is_lower_bound || lb.unsummed_services.length === 0) return null;
  const names = lb.unsummed_services
    .map((s) => s.service_name ?? s.system_id)
    .join(", ");
  return (
    <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-2.5 text-xs">
      <span className="text-amber-400 font-medium">Safest is a lower bound.</span>{" "}
      The composed sum ({fmtA(lb.true_max_sum_a)}) excludes{" "}
      {lb.unsummed_services.length} un-metered service
      {lb.unsummed_services.length === 1 ? "" : "s"} with no roster number:{" "}
      <span className="text-amber-300">{names}</span>. Real demand is higher
      than this figure by their contribution.
    </div>
  );
}

// -----------------------------------------------------------------------------
// Panel
// -----------------------------------------------------------------------------

export function RosterPanel({
  windowId, hasDeclaredShow, safestLowerBound,
}: {
  windowId: string;
  // True when the event has at least one declared show window — drives the
  // R9 pending-copy split.
  hasDeclaredShow: boolean;
  // From the suggestions response (U8). Null on the early-return paths.
  safestLowerBound: SafestLowerBound | null;
}) {
  const state = useRosterStore((s) => s.state);
  const load = useRosterStore((s) => s.load);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    load(windowId);
  }, [windowId, load]);

  // Reset selection when switching events — a selection from window A is
  // meaningless against window B's roster.
  useEffect(() => {
    setSelected(new Set());
  }, [windowId]);

  function toggle(membershipId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(membershipId)) next.delete(membershipId);
      else next.add(membershipId);
      return next;
    });
  }

  // Stable sorted array — referential identity changes only when the set
  // itself changes, so SubsetCoincidencePreview's reset-on-change useEffect
  // doesn't fire on every render.
  const selectedIds = useMemo(
    () => Array.from(selected).sort(),
    [selected],
  );

  // Non-admin / 403 — the panel is admin-only; hide it entirely.
  if (state.kind === "forbidden") return null;

  // Loading skeleton.
  if (state.kind === "loading" || state.kind === "idle") {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Load roster</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-8 rounded bg-muted/40 animate-pulse"
              />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error — with a retry affordance.
  if (state.kind === "error") {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Load roster</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center gap-4">
          <p className="text-sm text-red-400">
            Could not load the roster — {state.message}
          </p>
          <Button size="sm" variant="outline" onClick={() => load(windowId)}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { data } = state;

  // The worker has not run yet — computed_at is null, no rows materialized.
  if (data.computed_at === null) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Load roster</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <p className="text-sm text-muted-foreground">
            The roster has not been computed for this event yet. The roster
            worker materializes it on a ~30-minute cadence, and immediately
            after a show window is declared or tightened.
          </p>
          <Button size="sm" variant="outline" onClick={() => load(windowId)}>
            Check again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <CardTitle className="text-sm">
            Load roster ({data.entries.length})
          </CardTitle>
          {data.coincidence_peak_a != null && (
            <span className="text-xs text-muted-foreground">
              event coincidence peak {fmtA(data.coincidence_peak_a)}
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          The roster is the <span className="italic">planning</span> input —
          per-service trusted baselines from declared show windows. The audit
          table above shows <span className="italic">current observed</span>{" "}
          load; the two will differ, and that is expected.
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {safestLowerBound && <SafestLowerBoundNote lb={safestLowerBound} />}

        {data.entries.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No services on the roster for this event.
          </p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" />
                  <TableHead>Service</TableHead>
                  <TableHead className="text-right">True-max</TableHead>
                  <TableHead className="text-right">Show-condition</TableHead>
                  <TableHead className="text-right">State</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.entries.map((e) => (
                  <RosterRow
                    key={e.membership_id}
                    entry={e}
                    hasDeclaredShow={hasDeclaredShow}
                    selected={selected.has(e.membership_id)}
                    onToggle={toggle}
                    selectable={
                      e.data_state === "ok"
                      || e.data_state === "low_confidence"
                      || e.data_state === "moderate_confidence"
                    }
                  />
                ))}
              </TableBody>
            </Table>

            <SubsetCoincidencePreview
              windowId={windowId}
              selectedIds={selectedIds}
              entryName={(mid) => {
                const hit = data.entries.find((e) => e.membership_id === mid);
                return hit?.service_name ?? mid;
              }}
            />
          </>
        )}

        <p className="text-[10px] text-muted-foreground">
          Roster as of {fmtStamp(data.computed_at)}.
        </p>
      </CardContent>
    </Card>
  );
}
